if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        // Add drag scrolling for thumbnail layout
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail')) {
          this.initDragScrolling();
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        
        // Check if we're using thumbnail layout (vertical thumbnails on left)
        const isThumbnailLayout = this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail');
        const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
        
        if (isThumbnailLayout && thumbnailList) {
          // For vertical thumbnail layout, scroll to top of thumbnail container
          // Use getBoundingClientRect to get accurate position
          const thumbnailRect = thumbnail.getBoundingClientRect();
          const containerRect = thumbnailList.getBoundingClientRect();
          const currentScrollTop = thumbnailList.scrollTop;
          
          // Calculate the position of thumbnail relative to the container
          const thumbnailTop = thumbnailRect.top - containerRect.top + currentScrollTop;
          
          thumbnailList.scrollTo({
            top: thumbnailTop,
            behavior: 'smooth'
          });
        } else {
          // Original horizontal scrolling behavior
          if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
          if (this.elements.thumbnails.slider) {
            this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
          }
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initDragScrolling() {
        const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (!thumbnailList) return;

        let isDragging = false;
        let startY = 0;
        let startScrollTop = 0;
        let hasMoved = false;
        let clickedButton = null;
        let rafId = null;
        const dragThreshold = 3; // Minimum pixels to move before considering it a drag

        const handleDrag = (e) => {
          if (!isDragging) return;
          
          const currentY = e.clientY || (e.touches && e.touches[0].clientY);
          if (currentY === undefined) return;
          
          const deltaY = currentY - startY;
          const absDeltaY = Math.abs(deltaY);
          
          if (absDeltaY > dragThreshold) {
            hasMoved = true;
            // Calculate new scroll position based on mouse movement
            const newScrollTop = startScrollTop - deltaY;
            
            // Use requestAnimationFrame for smooth scrolling
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
              thumbnailList.scrollTop = newScrollTop;
            });
            
            if (e.preventDefault) e.preventDefault();
          }
        };

        // Mouse events - allow dragging from anywhere, including buttons
        thumbnailList.addEventListener('mousedown', (e) => {
          isDragging = true;
          hasMoved = false;
          clickedButton = e.target.closest('button.thumbnail');
          thumbnailList.style.cursor = 'grabbing';
          thumbnailList.style.userSelect = 'none';
          startY = e.clientY;
          startScrollTop = thumbnailList.scrollTop;
          
          // Add mousemove listener to document for smooth dragging outside container
          document.addEventListener('mousemove', handleDrag);
        });

        document.addEventListener('mouseup', (e) => {
          if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
          
          document.removeEventListener('mousemove', handleDrag);
          
          if (isDragging) {
            isDragging = false;
            thumbnailList.style.cursor = 'grab';
            thumbnailList.style.userSelect = '';
            
            // Prevent click if user was dragging
            if (hasMoved && clickedButton) {
              e.preventDefault();
              e.stopPropagation();
              clickedButton.blur();
            }
            
            clickedButton = null;
            hasMoved = false;
          }
        });

        // Also handle mouse leave
        thumbnailList.addEventListener('mouseleave', () => {
          if (isDragging) {
            // Keep dragging active even if mouse leaves, for smooth experience
            // Only stop if mouse is released
          }
        });

        // Touch events for mobile
        let touchStartButton = null;
        let touchStartScrollTop = 0;
        let touchStartY = 0;
        let touchHasMoved = false;

        thumbnailList.addEventListener('touchstart', (e) => {
          touchStartY = e.touches[0].clientY;
          touchStartScrollTop = thumbnailList.scrollTop;
          touchHasMoved = false;
          touchStartButton = e.target.closest('button.thumbnail');
        }, { passive: true });

        thumbnailList.addEventListener('touchmove', (e) => {
          const touchY = e.touches[0].clientY;
          const deltaY = touchY - touchStartY;
          const absDeltaY = Math.abs(deltaY);
          
          if (absDeltaY > dragThreshold) {
            touchHasMoved = true;
            const newScrollTop = touchStartScrollTop - deltaY;
            
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
              thumbnailList.scrollTop = newScrollTop;
            });
          }
        }, { passive: true });

        // Prevent thumbnail click if user was scrolling
        thumbnailList.addEventListener('touchend', (e) => {
          if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
          
          if (touchHasMoved && touchStartButton) {
            e.preventDefault();
            e.stopPropagation();
            touchStartButton.blur();
          }
          touchStartButton = null;
          touchHasMoved = false;
        }, { passive: false });
      }
    }
  );
}
